import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as ds from '@ulule/owl-kit-components/next'
import { styles as S, COLORS } from '@ulule/owl-kit-components/next'

import { RootState } from '../../../../../reducers'
import { CommentList } from '../../../../../reducers/comments'
import { Comment } from './Comment'
import { CommentForm } from './CommentForm'
import * as commentAction from '../../../../../actions/comments'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'

type CommentNodeProps = {
  comment: api.Comment<boolean>
  parentId: number | 'root'
}

export function CommentNode({ comment, parentId }: CommentNodeProps): React.ReactElement<CommentNodeProps> {
  const dispatch = Redux.useDispatch()
  const children = Redux.useSelector<RootState, CommentList | undefined>(
    (state) => state.application.comments.list[comment.id],
  )

  const [isFormOpen, setIsFormOpen] = React.useState(false)

  const hasMore = !!children && children.next !== null && children.size > children.comments.length

  const auth = useAuth()

  return (
    <Wrapper>
      <Comment
        comment={comment}
        onReply={() => setIsFormOpen(true)}
        onDelete={() => dispatch(commentAction.remove(comment, parentId))}
      />
      <Children>
        {children && (
          <>
            {children.comments.map((childComment) => {
              return (
                <Comment
                  key={childComment.id}
                  comment={childComment}
                  onReply={() => setIsFormOpen(true)}
                  onDelete={() => dispatch(commentAction.remove(childComment, comment.id))}
                />
              )
            })}
            {hasMore && (
              <ds.LinkAsButton
                onClick={() => children.next && dispatch(commentAction.loadReply(comment, children.next))}
              >
                {t('View more replies')}
              </ds.LinkAsButton>
            )}
            {children.newComments.map((childComment) => {
              return (
                <Comment
                  key={childComment.id}
                  comment={childComment}
                  onReply={() => setIsFormOpen(true)}
                  onDelete={() => dispatch(commentAction.remove(childComment, comment.id))}
                />
              )
            })}
          </>
        )}
        {isFormOpen && auth.type === 'loggedin' && (
          <CommentForm
            autoFocus={true}
            user={auth.user}
            onSubmit={async (values) => {
              await dispatch(commentAction.createReply(values.comment, comment.id))
              setIsFormOpen(false)
            }}
            onCancel={() => setIsFormOpen(false)}
            submitLabel={t('Reply')}
          />
        )}
      </Children>
    </Wrapper>
  )
}

const Children = styled.div`
  padding-left: 40px;
`

const Wrapper = styled.div`
  border-top: 1px solid ${ds.COLORS.GREY_SHADE_5};
  margin-top: -1px;
  border-bottom: 1px solid ${ds.COLORS.GREY_SHADE_5};
  padding-top: 10px;
`
